import { PropsWithChildren } from 'react';
import { Column } from '@tanstack/react-table';
import { TableCell, cn } from '@sib/ui';
import {
  getTableColumnBorderVariable,
  getTableColumnFixedSizingStyle,
  getTableColumnPinningProperties,
  getTableColumnWidth,
} from '../../utils';
import {
  borderLeftPinnedColumnBg,
  borderRightPinnedColumnBg,
  pinnedColumnBg,
  pinnedEdgeShadowLeftStyle,
  pinnedEdgeShadowRightStyle,
} from './DataTableBody.style';

interface DataTablePinnedCellProps<T> extends PropsWithChildren {
  column: Column<T>;
  withFixedColumnWidth: boolean;
  className?: string;
}

export const DataTablePinnedCell = <T,>({
  column,
  withFixedColumnWidth,
  className: extraClassName,
  children,
}: DataTablePinnedCellProps<T>) => {
  const { pinPosition, isLeftPinned, isRightPinned, shouldApplyShadow, style } =
    getTableColumnPinningProperties(column);

  const { meta } = column.columnDef;

  const columnWidthStyle = withFixedColumnWidth
    ? getTableColumnFixedSizingStyle(column.id)
    : {
        minWidth: 0,
        width: getTableColumnWidth(column.id),
      };

  return (
    <TableCell
      data-testid={meta?.cellDataTestId}
      className={cn(
        extraClassName,
        '[&>button]:w-full',
        pinPosition && 'sticky z-10',
        pinPosition && !shouldApplyShadow && pinnedColumnBg,
        isLeftPinned && shouldApplyShadow && borderLeftPinnedColumnBg,
        isRightPinned && shouldApplyShadow && borderRightPinnedColumnBg
      )}
      style={{
        ...style,
        ...columnWidthStyle,
        borderRight: `var(${getTableColumnBorderVariable(column.id)})`,
      }}
    >
      {children}
      {isLeftPinned && shouldApplyShadow && (
        <span style={pinnedEdgeShadowLeftStyle} />
      )}
      {isRightPinned && shouldApplyShadow && (
        <span style={pinnedEdgeShadowRightStyle} />
      )}
    </TableCell>
  );
};
